import { AccessProHelpModal } from '../core/HelpModal.js';
import { aswGetInstanceId, initWix } from '../platform/wix.js';
import { aswAppendIcon } from '../utils/iconUtils.js';

function isVisitorPreferenceKey(key) {
    if (typeof key !== 'string') return false;
    if (key.startsWith('acp-')) return true;
    // Feature choices use accesspro-*. Cookie consent uses the same prefix and must stay.
    if (key.startsWith('accesspro-') && !key.startsWith('accesspro-cookie-consent')) return true;
    return false;
}

/** Merchant turned off preference storage: drop saved visitor choices and stop writing new ones. */
function disableVisitorPreferenceStorage() {
    if (window.__acpPreferenceStorageDisabled) return;
    window.__acpPreferenceStorageDisabled = true;

    [localStorage, sessionStorage].forEach((store) => {
        const keys = [];
        for (let i = 0; i < store.length; i++) {
            const key = store.key(i);
            if (isVisitorPreferenceKey(key)) keys.push(key);
        }
        keys.forEach((key) => store.removeItem(key));

        const getItem = store.getItem.bind(store);
        const setItem = store.setItem.bind(store);
        const removeItem = store.removeItem.bind(store);
        store.getItem = (key) => (isVisitorPreferenceKey(key) ? null : getItem(key));
        store.setItem = (key, value) => {
            if (isVisitorPreferenceKey(key)) return;
            setItem(key, value);
        };
        store.removeItem = (key) => {
            if (isVisitorPreferenceKey(key)) return;
            removeItem(key);
        };
    });
}

class AccessProIcon {
    constructor() {
        this.accessProInstance = null;
        this.isLoading = false;
        this.complianceData = {}
        this.enableMenuSettings = false;
        this.settings = {};
        this.filterSettings = {};
        this.user = {};
        this.widgetStyle = "compact"
        this.pendingOpen = false

        // Initialize Wix platform variables immediately (sets accessProProxyHandle)
        initWix();

        if (!this.shouldBeHidden()) {
            // Now accessProProxyHandle is guaranteed to be set, so we can proceed directly
            this.init();
        }
    }

    //==========================
    // Check if the widget should be hidden
    //==========================
    shouldBeHidden() {
        const isHiddenForever = localStorage.getItem('acp-widget-hidden') === 'true';
        const hiddenUntil = localStorage.getItem('acp-widget-hidden-until');
        const isHiddenTemporarily = hiddenUntil && parseInt(hiddenUntil) > new Date().getTime();
        const isHiddenForSession = sessionStorage.getItem('acp-widget-hidden') === 'true';
        // Remove expired hidden-until if exists
        if (hiddenUntil && parseInt(hiddenUntil) <= new Date().getTime()) {
            localStorage.removeItem('acp-widget-hidden-until');
            return false;
        }
        return isHiddenForever || isHiddenTemporarily || isHiddenForSession;
    }

    checkHiddenState(isHideFloatingIconEnabled) {
        if (this.shouldBeHidden() || isHideFloatingIconEnabled) {
            const style = document.createElement("style");
            style.innerHTML = "#Access-Pro-Icon { display: none; }";
            document.head.appendChild(style);
        }
    }


    //==========================
    // Initialize the widget
    //==========================
    async init() {
        // initWix() is now called in constructor, so we don't need to call it here
        let showWidget = true;
        this.config = await this.fetchConfig();
        if (this.config?.data?.storage_consent === false) {
            disableVisitorPreferenceStorage();
        }
        const isHideFloatingIconEnabled = this.config?.data?.hide_floating_icon;
        this.checkHiddenState(isHideFloatingIconEnabled);

        if (!showWidget) {
            return;
        }
        this.isWidgetOpenedOnce = localStorage.getItem("acp-widget-opened") === "true";
        if (this.config?.success) {
            this.widgetStyle = this.config.data?.template || "default";

            const showWidget = await this.filerCountries({
                location_filter: this.config.data?.location_filter || "",
                filter_countries: this.config.data?.filter_countries || [],
            })

            const customCSS = this.config.data?.custom_css || "";
            if (customCSS) {
                const style = document.createElement("style");
                style.innerHTML = customCSS;
                document.head.appendChild(style);
            }

            if (!showWidget) {
                return;
            }

            try {
                await import('../styles/main.css');
            } catch (e) {
                console.error(e);
            }
            const allowByQuota =
                this.config.decremented === true ||
                this.config.remainingPageViews == null ||
                this.config.remainingPageViews > 0;
            if (!allowByQuota) {
                return;
            }

            this.addEscKeyListener();

            if (["compact", "minimal", "modern"].includes(this.widgetStyle)) {
            } else {
                const acpElement = document.getElementById("acp-widget");
                if (acpElement) {
                    acpElement.style.display = "none";
                }
            }

            this.settings = this.mergeSettings(this.config.data);
            this.complianceData = this.config.data?.compliance;
            this.user = this.config?.user;
            this.render({
                icon: this.settings.icon,
                icon_color_fill: this.settings.icon_color_fill,
                icon_color_stroke: this.settings.icon_color_stroke,
                icon_position: this.settings.icon_position,
                icon_style: this.settings.icon_style,
                icon_size: this.settings.icon_size,
                custom_position: this.settings.custom_position,
                hidewidget: this.settings.enableMenuSettings?.hidewidget,
            });

            this.injectOpenWidgetButton();

            if (this.config?.data?.storage_consent !== false && this.hasSavedAccessibilityState()) {
                this.loadJavaScript(false).catch((error) => {
                    console.error("Error restoring accessibility features:", error);
                });
            }

        } else {
            return;
        }
    }

    hasSavedAccessibilityState() {
        const ignore = new Set([
            'acp-widget-opened',
            'acp-widget-size',
            'acp-widget-position',
            'acp-widget-hidden',
            'acp-widget-hidden-until',
            'acp-active-language',
            'acp-shortcuts-enabled',
        ]);
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!isVisitorPreferenceKey(key) || ignore.has(key)) continue;
            const value = localStorage.getItem(key);
            if (value && value !== 'false' && value !== 'normal' && value !== '1' && value !== 'none') {
                return true;
            }
        }
        return false;
    }

    //==========================
    // Render the icon
    //==========================
    render(iconSettings) {
        aswAppendIcon(iconSettings, this.handleHideWidgetIconClick);
    }


    //==========================
    // Load the JavaScript
    //==========================
    loadJavaScript(openAfterLoad = false) {
        this.isLoading = true;
        return new Promise(async (resolve, reject) => {
            try {
                if (["compact", "minimal", "modern", "default"].includes(this.widgetStyle)) {
                    const accessProRenderWidget = await this.loadWidgetModule();
                    const widgetInstance = await accessProRenderWidget(this.settings, this.complianceData, this.user);
                    this.accessProInstance = widgetInstance;

                    if (this.accessProInstance && (openAfterLoad || this.pendingOpen)) {
                        if (!this.accessProInstance.isOpen) {
                            this.accessProInstance.toggleWidget();
                        }
                        this.pendingOpen = false;
                    }

                    // Remove the features containers if they are empty
                    const featuresContainers = document.querySelectorAll(".acp-features");
                    featuresContainers.forEach(container => {
                        const itemsContainer = container.querySelector(".acp-features-list");
                        if (itemsContainer && itemsContainer.innerHTML === '') {
                            container.remove();
                        }
                    });

                    // Add keyboard event listener for accessibility
                    const accessProElement = document.querySelector('.accesspro');
                    if (accessProElement) {
                        accessProElement.addEventListener('keydown', function (event) {
                            const target = event.target;
                            if (
                                target.getAttribute('role') === 'button' &&
                                target.getAttribute('tabindex') === '0'
                            ) {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault(); // Prevent scrolling on space
                                    target.click(); // Trigger the click
                                }
                            }
                        });
                    }

                    this.isLoading = false;
                    resolve();
                } else {
                    // For other widget styles, use the old method if available
                    if (typeof aswFetchConfigs === 'function') {
                        aswFetchConfigs(this.response);
                    }
                    this.isLoading = false;
                    resolve();
                }
            } catch (error) {
                this.isLoading = false;
                console.error("Error rendering widget:", error);
                reject(error);
            }
        });
    }

    //==========================
    // Load the widget module
    //==========================
    async loadWidgetModule() {
        // Memoize to ensure the module is loaded only once
        if (!this.widgetModulePromise) {
            this.widgetModulePromise = (async () => {
                if (!window.ACCESSPRO_ICONS) {
                    const iconsModule = await import('../assets/icons.js');
                    window.ACCESSPRO_ICONS = iconsModule.ACCESSPRO_ICONS;
                }
                const widgetModule = await import('../widget/Widget.js');
                return widgetModule.accessProRenderWidget;
            })().catch((error) => {
                console.error("Error loading widget module:", error);
                throw error;
            });
        }
        return await this.widgetModulePromise;
    }

    //==========================
    // On click
    //==========================
    async onClick() {

        if (this.isLoading) {
            this.pendingOpen = true;
            return;
        }
        this.isLoading = true; // Set loading state
        const loaderButton = document.querySelector(".acp-loader-button");
        loaderButton.style.display = "block"; // Show loader
        try {
            await this.loadJavaScript(true);
        } catch (error) {
            console.error("Error loading JavaScript:", error);
        } finally {
            loaderButton.style.display = "none"; // Hide loader
            this.isLoading = false; // Reset loading state  
        }


    }


    //==========================
    // Toggle the widget
    //==========================
    toggleWidget() {

        if (this.accessProInstance) {
            this.accessProInstance.toggleWidget();
        } else {
            this.onClick();
        }
    }

    //==========================
    // Handle hide widget icon click
    //==========================

    handleHideWidgetIconClick = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const triggerModalFlow = () => {
            const hideButton = document.querySelector('.acp-hide-widget-button');
            if (hideButton) {
                hideButton.click();
                return true;
            }
            return false;
        };

        // Try to use the existing widget hide button if present
        if (triggerModalFlow()) return;

        // Ensure the widget JS is loaded so the modal class is available
        if (!this.accessProInstance) {
            try {
                await this.loadJavaScript();
            } catch (error) {
                console.error('Error loading widget for hide action:', error);
            }
        }

        // After load, try again via the existing button (if widget DOM is now rendered)
        if (triggerModalFlow()) return;

        // Fallback: replicate the hide modal directly
        const texts = {
            hideWidget: (this.accessProInstance?.translations?.["HIDE WIDGET"]) || "Hide Widget",
            hideWidgetDesc: (this.accessProInstance?.translations?.["HIDE WIDGET DESC"]) || "How long would you like to hide the widget?",
            forCurrentSession: (this.accessProInstance?.translations?.["FOR CURRENT SESSION"]) || "For current session",
            for24Hours: (this.accessProInstance?.translations?.["FOR 24 HOURS"]) || "For 24 hours",
            forOneWeek: (this.accessProInstance?.translations?.["FOR ONE WEEK"]) || "For one week",
            forever: (this.accessProInstance?.translations?.["FOREVER"]) || "Forever (until local storage is cleared)",
        };

        if (typeof AccessProHelpModal !== "function") {
            // If the modal constructor is still unavailable, quick-hide the icon
            sessionStorage.setItem('acp-widget-hidden', 'true');
            localStorage.removeItem('acp-widget-hidden-until');
            const widgetIcon = document.querySelector('#Access-Pro-Icon');
            if (widgetIcon) widgetIcon.style.display = 'none';
            return;
        }

        const modal = new AccessProHelpModal({
            id: 'hide-widget-modal',
            title: texts.hideWidget,
            titleTranslationKey: 'HIDE WIDGET',
            contentHTML: `
          <div class="acp-hide-widget-options">
            <span data-ap-translate="HIDE WIDGET DESC">${texts.hideWidgetDesc}</span>
            <select id="hide-duration-select" class="acp-hide-duration-select">
              <option value="session" data-ap-translate="FOR CURRENT SESSION">${texts.forCurrentSession}</option>
              <option value="day" data-ap-translate="FOR 24 HOURS">${texts.for24Hours}</option>
              <option value="week" data-ap-translate="FOR ONE WEEK">${texts.forOneWeek}</option>
              <option value="forever" data-ap-translate="FOREVER">${texts.forever}</option>
            </select>
            <div style="text-align: center; margin-top: 20px;">
              <div role="button" tabindex="0" id="confirm-hide-widget" class="acp-hide-option" data-ap-translate="HIDE WIDGET">${texts.hideWidget}</div>
            </div>
          </div>
        `
        });

        modal.show();

        const confirmButton = document.getElementById('confirm-hide-widget');
        const durationSelect = document.getElementById('hide-duration-select');

        if (confirmButton && durationSelect) {
            confirmButton.addEventListener('click', () => {
                const duration = durationSelect.value;
                const now = new Date().getTime();

                switch (duration) {
                    case 'session':
                        sessionStorage.setItem('acp-widget-hidden', 'true');
                        break;
                    case 'day':
                        localStorage.setItem('acp-widget-hidden-until', now + (24 * 60 * 60 * 1000));
                        break;
                    case 'week':
                        localStorage.setItem('acp-widget-hidden-until', now + (7 * 24 * 60 * 60 * 1000));
                        break;
                    case 'forever':
                        localStorage.setItem('acp-widget-hidden', 'true');
                        break;
                }

                const widgetIcon = document.querySelector('#Access-Pro-Icon');
                const widget = document.querySelector('.asw-widget');
                if (widgetIcon) {
                    widgetIcon.style.display = 'none';
                    widget.style.display = 'none';
                }

                if (this.accessProInstance?.isOpen) {
                    this.accessProInstance.toggleWidget();
                }

                modal.close();
            });
        }
    }

    //==========================
    // Fetch the config from the server (POST also decrements page views)
    //==========================
    async fetchConfig() {
        try {
            const instanceId = aswGetInstanceId();
            const response = await fetch(accessProSettingsUrl, {
                method: "POST",
                headers: {
                    ...(instanceId ? { Authorization: instanceId } : {}),
                    "Content-Type": "application/json",
                    "origin": "access-pro-bigcommerce.entanglecommerce.com"
                },
                body: JSON.stringify(
                    instanceId
                        ? { store_hash: instanceId, instance: instanceId, domain: window.location.hostname }
                        : { domain: window.location.hostname }
                ),
            });

            const data = await response.json();

            const success = data?.success || false;

            if (!success) {
                console.error('Error fetching config:', data);
                return null;
            } else {
                return {
                    success: true,
                    user: data.user || {},
                    decremented: data.decremented,
                    remainingPageViews: data.remainingPageViews,
                    data: {
                        ...data.data?.data || {},
                        compliance: {
                            compliance_title: data.data?.compliance_title || "Accessibility Menu",
                            compliance_content: data.data?.compliance_content || "This website is designed to be accessible for all users.",
                        }
                    }

                }
            }

        } catch (error) {
            console.error('Error fetching config:', error);
            return null;
        }
    }


    //==========================
    // Merge the settings with the default settings
    //==========================
    mergeSettings = (settings = {}) => {
        const {
            enableMenuSettings = {},
            enable_features = true,
            icon = "default",
            icon_color_fill = "#23185c",
            icon_color_stroke = "#23185c",
            icon_position = "BL",
            icon_style = "thick",
            widgetheight = "full",
            icon_size = "default",
            menu_content_background_color = "#eff1f5",
            menu_content_color = "#111111",
            menu_header_background_color = "#23185c",
            menu_header_font_color = "#f2f2f2",
            open_accessibility_profile = true,
            selected_languages = ["en", "fr", "es", "de"],
            default_menu_language = "en",
            widget_size = "large",
            template = "minimal",
            remove_branding = false,
            custom_position = {
                top: "20",
                left: "20",
                bottom: "20",
                right: "20",
            },
            statement_action = "popup",
            statement_redirect_url = "",
            custom_css = "",
        } = settings;

        const defaultMenuSettings = {
            alignment: true,
            animation: true,
            colorfilters: true,
            contrast: true,
            cursor: true,
            dyslexic: true,
            fontscale: true,
            fontweight: true,
            headings: true,
            images: true,
            languages: true,
            letterspacing: true,
            linespacing: true,
            links: true,
            magnifier: true,
            mission: true,
            profiles: true,
            readingguide: true,
            readingmask: true,
            reset: true,
            saturation: true,
            sounds: true,
            text2speech: true,
            describeimages: false,
            videos: true,
            pagestructure: true,
            textsummary: true,
            virtualkeyboard: true,
            invertcolors: true,
            translatepage: true,
            readerprofile: true,
            seizureprofile: true,
            adhdprofile: true,
            cognitiveprofile: true,
            visuallyimpairedprofile: true,
            motorimpairmentprofile: false,
            elderlyprofile: false,
            colorblindprofile: false,
            customcolors: false,
            linknavigation: false,
            voicenavigation: true,
            keyboardnavigation: false,
            talkandtype: true,
        };

        const mergedMenuSettings = { ...defaultMenuSettings, ...enableMenuSettings };

        return {
            enable_features,
            icon,
            icon_color_fill,
            icon_color_stroke,
            icon_position,
            icon_style,
            icon_size,
            menu_content_color,
            menu_header_background_color,
            menu_content_background_color,
            menu_header_font_color,
            open_accessibility_profile,
            selected_languages,
            default_menu_language,
            widget_size,
            custom_position,
            template,
            widgetheight,
            remove_branding,
            enableMenuSettings: mergedMenuSettings,
            statement_action,
            statement_redirect_url,
            custom_css,
        };
    };

    //==========================
    // Get the visitor country
    //==========================
    aswGetVisitorCountry = async () => {
        try {
            // Return cached result for the session to avoid a repeated network call
            const cached = sessionStorage.getItem('acp-visitor-country');
            if (cached !== null) return cached;

            var _data$detected_values;
            const response = await fetch('/browsing_context_suggestions.json');
            const data = await response.json();
            if (!data) return "";
            const countryCode = data === null || data === void 0 || (_data$detected_values = data.detected_values) === null || _data$detected_values === void 0 || (_data$detected_values = _data$detected_values.country) === null || _data$detected_values === void 0 ? void 0 : _data$detected_values.handle;
            sessionStorage.setItem('acp-visitor-country', countryCode || "");
            return countryCode;
        } catch (er) {
            return "";
        }
    };

    //==========================
    // Filter the countries
    //==========================
    filerCountries = async ({
        location_filter,
        filter_countries
    }) => {
        const filterSettings = location_filter
        if (["hide", "show"].includes(filterSettings)) {
            const countriesToFilter = filter_countries || [];
            if (countriesToFilter.length !== 0) {
                const visitorCountry = await this.aswGetVisitorCountry();
                if (filterSettings === "hide" && countriesToFilter.includes(visitorCountry)) {
                    return false;
                }
                if (filterSettings === "show" && !countriesToFilter.includes(visitorCountry)) {
                    return false;
                }
            }
        }
        return true;
    }


    //==========================
    // Inject the open widget button
    //==========================
    injectOpenWidgetButton() {
        // Create the button element
        const widgetButton = document.createElement("a");
        widgetButton.href = "#";
        widgetButton.textContent = "Toggle AccessPro Widget";
        widgetButton.setAttribute("tabindex", "0");
        widgetButton.setAttribute("role", "button");
        widgetButton.classList.add("asw-widget-button-top");

        // Add event listeners
        widgetButton.addEventListener("focus", () => {
            widgetButton.classList.add("asw-widget-button-top-focused");
        });
        widgetButton.addEventListener("blur", () => {
            widgetButton.classList.remove("asw-widget-button-top-focused");
        });
        widgetButton.addEventListener("click", e => {
            e.preventDefault();
            this.toggleWidget();

            // Focus the close button if the widget is opened
            if (this.accessProInstance) {
                const closeButton = document.querySelector(".acp-close");
                if (closeButton) {
                    closeButton.focus();
                }
            }
        });

        // Prepend the button to the body
        document.body.prepend(widgetButton);
    }

    //==========================
    // Add the esc key listener
    //==========================
    addEscKeyListener() {
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && this.accessProInstance) {
                if (this.accessProInstance?.isOpen) {
                    this.accessProInstance.toggleWidget();
                }
            }
        });
    }




}


//==========================
// Initialize the accessibility icon
//==========================
export default function initAccessibilityIcon() {
    if (!window.iconInstance) {
        window.iconInstance = new AccessProIcon();
    }
}
